import type { IJsonModel } from 'flexlayout-react'

interface LayoutThumbnailProps {
  model: IJsonModel
  className?: string
}

// Minimal structural view of flexlayout json nodes; accepts both
// serialized IJsonRowNode trees and the hand-built presets that use
// type:'column'.
interface PreviewNode {
  type?: string
  weight?: number
  id?: string
  component?: string
  children?: PreviewNode[]
}

function NodePreview({
  node,
  direction,
}: {
  node: PreviewNode
  direction: 'row' | 'column'
}) {
  const weight = node.weight ?? 50

  if (node.type === 'tabset' || !node.children?.length) {
    return (
      <div
        className="rounded-sm bg-muted/60 border border-border/40"
        style={{
          flex: weight,
          minWidth: 0,
          minHeight: 0,
        }}
      />
    )
  }

  // Hand-built presets use type:'column' to explicitly mark vertical direction.
  // Serialized layouts (model.toJson()) always use type:'row' and rely on
  // alternating parent→child orientation. Support both by using the direction
  // prop (which alternates at each level) unless the node explicitly says 'column'.
  const nodeDirection = node.type === 'column' ? 'column' : direction
  const childDirection = nodeDirection === 'row' ? 'column' : 'row'

  return (
    <div
      className="flex gap-0.5"
      style={{
        flex: weight,
        flexDirection: nodeDirection,
        minWidth: 0,
        minHeight: 0,
      }}
    >
      {node.children.map((child: PreviewNode, index: number) => (
        <NodePreview
          key={`${index}-${child.type}-${child.id || child.component || ''}`}
          node={child}
          direction={childDirection}
        />
      ))}
    </div>
  )
}

export function LayoutThumbnail({
  model,
  className = '',
}: LayoutThumbnailProps) {
  const layout = model.layout
  if (!layout) return null

  return (
    <div
      className={`flex aspect-[4/3] w-full overflow-hidden rounded-md border border-border/60 bg-background p-1 ${className}`}
    >
      <NodePreview node={layout} direction="row" />
    </div>
  )
}
